import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  Grid2,
  InputAdornment,

  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {  HiOutlinePhotograph } from "react-icons/hi";
import { FiFilm } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import MaInput from "../../../../Components/UI/ma-input";
import MaSwitch from "../../../../Components/UI/ma-switch";
import MaButton from "../../../../Components/UI/ma-button";
import MaSearchableSelect from "../../../../Components/UI/ma-dropdown";
import gallery from "../../../../assets/Icons/dashboard/gallery.svg";
import {
  FETCH_PRODUCT_BY_ID,
  GET_CATEGORIES_N_SUB,
  GET_PRODUCT_ORIGIN,
} from "../../../../Utils/Queries";
import {
  DELETE_EXISTING_IMAGE,
  EDIT_PRODUCT_V3,
} from "../../../../Utils/Mutations";
import {
  uploadFileToCloudinary,
  uploadVideoToCloudinary,
} from "../../../../Utils/api";
import { MdLightbulb } from "react-icons/md";

const REQ = (
  <span className="text-red-500" aria-hidden>
    {" "}
    *
  </span>
);

const MAX_IMAGES = 5;
const MAX_VIDEOS = 2;


function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function formatTagsForInput(tags) {
  if (Array.isArray(tags)) {
    return tags
      .map((t) => String(t).trim())
      .filter(Boolean)
      .join(", ");
  }
  if (typeof tags === "string") return tags;
  return "";
}

function parseTagsFromInput(raw) {
  return String(raw ?? "")
    .split(/[,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseEmbedVideoUrls(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function priceInputValue(price) {
  if (price == null || price === "") return "";
  const n = Number(price);
  if (Number.isFinite(n)) return String(n);
  return String(price).replace(/[^\d.]/g, "");
}

function listingActiveFromProduct(p) {
  if (p?.isDeleted) return false;
  const s = String(p?.status ?? "").trim();
  if (!s) return true;
  const u = s.toUpperCase();
  if (u.includes("REJECT")) return false;
  return true;
}

function defaultOriginId(origins) {
  const items = origins ?? [];
  const ng = items.find((o) => /nigeria/i.test(String(o?.name ?? "")));
  return ng?._id ?? items[0]?._id ?? "";
}

function buildEditFormFromProduct(p) {
  const delivery =
    p.deliveryProcessingTime != null && p.deliveryProcessingTime !== ""
      ? String(p.deliveryProcessingTime)
      : "";
  return {
    productName: p.name ?? "",
    categoryId: p.categories?.[0]?._id ?? "",
    productOriginId: p.productOrigin?._id ?? "",
    sku: p.reference ?? "",
    description: p.description ?? "",
    tags: formatTagsForInput(p.tags),
    price: priceInputValue(p.price),
    discount: "",
    stock: String(p.quantityInStock ?? ""),
    lowStockAlert:
      p.lowStockAlert != null && p.lowStockAlert !== ""
        ? String(p.lowStockAlert)
        : "10",
    deliveryTime: delivery,
    weight: p.weight != null && p.weight !== "" ? String(p.weight) : "",
    listingActive: listingActiveFromProduct(p),
    embedVideoUrls: "",
  };
}

export default function EditProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const { data, loading, error, refetch } = useQuery(FETCH_PRODUCT_BY_ID, {
    variables: { productId: id ?? "" },
    skip: !id,
    fetchPolicy: "network-only",
  });

  const { data: catData, loading: categoriesLoading } =
    useQuery(GET_CATEGORIES_N_SUB);
  const { data: originData, loading: originsLoading } =
    useQuery(GET_PRODUCT_ORIGIN);

  const [removeImagesFromProduct] = useMutation(DELETE_EXISTING_IMAGE);
  const [editProductV3] = useMutation(EDIT_PRODUCT_V3);

  const product = data?.fetchProductById ?? null;
  const originItems = useMemo(
    () => originData?.getProductOrigins?.items ?? [],
    [originData],
  );

  const categoryOptions = useMemo(() => {
    const rows = catData?.fetchAllCategoriesAndTheirSubCategories;
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => row?.category)
      .filter((c) => c != null && c._id != null && c.isActive !== false);
  }, [catData]);

  const categorySelectOptions = useMemo(
    () =>
      categoryOptions
        .map((c) => ({
          label: String(c?.name ?? "").trim() || "—",
          value: String(c?._id ?? ""),
        }))
        .filter((o) => o.value),
    [categoryOptions],
  );

  const originSelectOptions = useMemo(
    () =>
      originItems
        .map((o) => ({
          label: String(o?.name ?? "").trim() || "—",
          value: String(o?._id ?? ""),
        }))
        .filter((o) => o.value),
    [originItems],
  );

  const [form, setForm] = useState({
    productName: "",
    categoryId: "",
    productOriginId: "",
    sku: "",
    description: "",
    tags: "",
    price: "",
    discount: "",
    stock: "",
    lowStockAlert: "",
    deliveryTime: "",
    weight: "",
    listingActive: false,
    embedVideoUrls: "",
  });

  const [serverImageUrls, setServerImageUrls] = useState([]);
  const [pendingImages, setPendingImages] = useState([]);
  const [serverVideoUrls, setServerVideoUrls] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [imageBusyUrl, setImageBusyUrl] = useState(null);

  useLayoutEffect(() => {
    if (!product) return;
    setForm(buildEditFormFromProduct(product));
    setServerImageUrls(
      Array.isArray(product.images) ? product.images.filter(Boolean) : [],
    );
    setPendingImages([]);
    setServerVideoUrls(
      Array.isArray(product.videoUrls) ? product.videoUrls.filter(Boolean) : [],
    );
    setPendingVideos([]);
  }, [product]);

  useEffect(() => {
    if (!product?._id) return;
    setForm((f) => {
      if (f.productOriginId) return f;
      const def = defaultOriginId(originItems);
      return def ? { ...f, productOriginId: def } : f;
    });
  }, [product?._id, originItems]);

  const nameLen = form.productName.length;

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const updateBool = (key) => (_, checked) =>
    setForm((f) => ({ ...f, [key]: checked }));

  const imageSlotsLeft =
    MAX_IMAGES - serverImageUrls.length - pendingImages.length;
  const videoCount =
    serverVideoUrls.length +
    pendingVideos.length +
    parseEmbedVideoUrls(form.embedVideoUrls).length;

  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || imageSlotsLeft <= 0) return;
    const take = files.slice(0, imageSlotsLeft);
    setPendingImages((prev) => [
      ...prev,
      ...take.map((file) => ({
        id: genId(),
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
  };

  const removePendingImage = (pid) => {
    setPendingImages((prev) => {
      const item = prev.find((i) => i.id === pid);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((i) => i.id !== pid);
    });
  };

  const removeServerImage = async (url) => {
    if (!id || !url) return;
    setImageBusyUrl(url);
    try {
      await removeImagesFromProduct({
        variables: { productId: id, images: [url] },
      });
      setServerImageUrls((prev) => prev.filter((u) => u !== url));
      enqueueSnackbar("Image removed from product.", { variant: "success" });
      await refetch?.();
    } catch (e) {
      const msg =
        e?.graphQLErrors?.[0]?.message ||
        e?.networkError?.message ||
        e?.message ||
        "Could not remove image.";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setImageBusyUrl(null);
    }
  };

  const onPickVideos = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const used =
      serverVideoUrls.length +
      pendingVideos.length +
      parseEmbedVideoUrls(form.embedVideoUrls).length;
    const remaining = MAX_VIDEOS - used;
    if (remaining <= 0) {
      enqueueSnackbar(`You can have at most ${MAX_VIDEOS} videos.`, {
        variant: "warning",
      });
      return;
    }
    const take = files.slice(0, remaining);
    setPendingVideos((prev) => [
      ...prev,
      ...take.map((file) => ({ id: genId(), file })),
    ]);
  };

  const removePendingVideo = (pid) => {
    setPendingVideos((prev) => prev.filter((i) => i.id !== pid));
  };

  const removeServerVideo = (url) => {
    setServerVideoUrls((prev) => prev.filter((u) => u !== url));
  };

  const validateSubmit = () => {
    const issues = [];
    if (!form.productName.trim()) issues.push("Enter a product name.");
    if (!form.categoryId) issues.push("Select a category.");
    if (!form.productOriginId) issues.push("Select a product origin.");
    if (!form.description.trim()) issues.push("Enter a description.");
    if (serverImageUrls.length + pendingImages.length < 1) {
      issues.push("Add at least one product image.");
    }
    const price = parseFloat(form.price.trim());
    const stock = parseInt(form.stock.trim(), 10);
    const low = parseInt(form.lowStockAlert.trim(), 10);
    const weight = parseFloat(form.weight.trim());
    const delivery = parseFloat(form.deliveryTime.trim());
    if (!form.price.trim() || !Number.isFinite(price) || price <= 0) {
      issues.push("Enter a valid price greater than 0.");
    }
    if (!form.stock.trim() || !Number.isFinite(stock) || stock < 0) {
      issues.push("Enter stock quantity (0 or more).");
    }
    if (!form.lowStockAlert.trim() || !Number.isFinite(low) || low < 0) {
      issues.push("Enter low stock alert (0 or more).");
    }
    if (!form.weight.trim() || !Number.isFinite(weight) || weight < 0) {
      issues.push("Enter weight (kg).");
    }
    if (
      !form.deliveryTime.trim() ||
      !Number.isFinite(delivery) ||
      delivery <= 0
    ) {
      issues.push(
        "Enter delivery processing time as a number (e.g. 2 for 2 days).",
      );
    }
    if (videoCount > MAX_VIDEOS) {
      issues.push(`At most ${MAX_VIDEOS} videos (files + URLs combined).`);
    }
    return issues;
  };

  const handleSave = async () => {
    setSubmitError("");
    const issues = validateSubmit();
    if (issues.length) {
      setSubmitError(issues.join(" "));
      return;
    }

    setSubmitting(true);
    try {
      const newImageUrls = [];
      for (let i = 0; i < pendingImages.length; i++) {
        const file = pendingImages[i].file;
        const response = await uploadFileToCloudinary(file, () => {});
        newImageUrls.push(response.data.secure_url);
      }

      const newVideoUrls = [];
      for (let i = 0; i < pendingVideos.length; i++) {
        const file = pendingVideos[i].file;
        const response = await uploadVideoToCloudinary(file, () => {});
        newVideoUrls.push(response.data.secure_url);
      }

      const embedUrls = parseEmbedVideoUrls(form.embedVideoUrls);
      const imageUrls = [...serverImageUrls, ...newImageUrls];
      const videoUrls = [
        ...serverVideoUrls,
        ...newVideoUrls,
        ...embedUrls,
      ].slice(0, MAX_VIDEOS);

      const payload = {
        productId: id,
        name: form.productName.trim(),
        description: form.description.trim(),
        categoryId: form.categoryId,
        productOriginId: form.productOriginId,
        price: parseFloat(form.price.trim()),
        quantityInStock: parseInt(form.stock.trim(), 10),
        imageUrls,
        videoUrls,
        tags: parseTagsFromInput(form.tags),
        weight: parseFloat(form.weight.trim()),
        deliveryProcessingTime: parseFloat(form.deliveryTime.trim()),
        lowStockAlert: parseInt(form.lowStockAlert.trim(), 10),
      };

      await editProductV3({ variables: { payload } });

      pendingImages.forEach((i) => {
        if (i.preview) URL.revokeObjectURL(i.preview);
      });
      setPendingImages([]);
      setPendingVideos([]);
      setForm((f) => ({ ...f, embedVideoUrls: "" }));

      enqueueSnackbar("Product updated.", { variant: "success" });
      await refetch();
    } catch (e) {
      const msg =
        e?.graphQLErrors?.[0]?.message ||
        e?.networkError?.message ||
        e?.message ||
        "Could not update product.";
      setSubmitError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!id) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4">
        <p className="text-[15px] text-[#6B7280]">Missing product id.</p>
        <MaButton
          title="Back to products"
          variant="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate("/seller/my-products")}
        />
      </div>
    );
  }

  if (loading && !product) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4">
        <CircularProgress size={36} sx={{ color: "#2E1834" }} />
        <p className="text-[14px] text-[#6B7280]">Loading product…</p>
      </div>
    );
  }

  if (error) {
    const msg =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.message ||
      error?.message ||
      "Could not load product.";
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4">
        <p className="max-w-md text-center text-[15px] text-[#DC2626]">{msg}</p>
        <MaButton
          title="Back to products"
          variant="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate("/seller/my-products")}
        />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4">
        <p className="text-[15px] text-[#6B7280]">Product not found.</p>
        <MaButton
          title="Back to products"
          variant="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate("/seller/my-products")}
        />
      </div>
    );
  }

  const displayName = product.name || form.productName || "Product";

  return (
    <div className="relative min-h-screen bg-[#F9FAFB] pb-20">
      {submitting ? (
        <div className="fixed inset-0 z-[50] flex flex-col items-center justify-center gap-3 bg-white/80">
          <CircularProgress size={40} sx={{ color: "#2E1834" }} />
          <p className="text-[14px] text-[#6B7280]">Saving…</p>
        </div>
      ) : null}

      <div className="mt-10">
        <header className="mb-8 md:mb-10">
          <h1 className="my-0 text-[22px] font-semibold tracking-tight text-[#0F0F0F] md:text-[24px]">
            Edit Product
          </h1>
          <p className="my-0 mt-2 max-w-2xl text-[14px] leading-relaxed text-[#6B7280]">
            Fill in the core details buyers will see on your listing.
          </p>
        </header>

        <Grid2 container spacing={4}>
          <Grid2 size={{ md: 6 }}>
            <div className="bg-white rounded-[16px] border border-solid border-[#EAEAEA80] pb-10">
              <section className="rounded-2xl border border-[#EFEFEF] bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.06)] md:p-8">
                <h2 className="my-0 mb-6 text-[15px] font-semibold text-[#1F2937]">
                  Product Information
                </h2>

                <div className="flex flex-col gap-5">
                  <MaInput
                    label={
                      <span>
                        Product Name
                        {REQ}
                      </span>
                    }
                    fullWidth
                    placeholder="e.g Basket"
                    value={form.productName}
                    onChange={update("productName")}
                    inputProps={{ maxLength: 80 }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <span className="text-[11px] text-[#9CA3AF] tabular-nums">
                              {nameLen}/80
                            </span>
                          </InputAdornment>
                        ),
                      },
                    }}
                    borderColor="#E5E7EB"
                  />

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <MaSearchableSelect
                      label={
                        <span>
                          Category
                          {REQ}
                        </span>
                      }
                      options={categorySelectOptions}
                      value={form.categoryId}
                      onChange={(nextId) =>
                        setForm((f) => ({ ...f, categoryId: nextId }))
                      }
                      disabled={categoriesLoading}
                      placeholder={
                        categoriesLoading ? "Loading…" : "Select category"
                      }
                      searchPlaceholder="Search categories…"
                      emptyMessage={
                        categoriesLoading ? "Loading…" : "No categories found."
                      }
                    />
                    <MaInput
                      label="SKU (reference)"
                      fullWidth
                      placeholder="Shown for your reference"
                      value={form.sku}
                      onChange={update("sku")}
                      disabled
                      borderColor="#E5E7EB"
                    />
                  </div>

                  <MaSearchableSelect
                    label={
                      <span>
                        Product Origin
                        {REQ}
                      </span>
                    }
                    options={originSelectOptions}
                    value={form.productOriginId}
                    onChange={(nextId) =>
                      setForm((f) => ({ ...f, productOriginId: nextId }))
                    }
                    disabled={originsLoading}
                    placeholder={originsLoading ? "Loading…" : "Select origin"}
                    searchPlaceholder="Search origins…"
                    emptyMessage={
                      originsLoading ? "Loading…" : "No origins found."
                    }
                  />

                  <MaInput
                    label={
                      <span>
                        Description
                        {REQ}
                      </span>
                    }
                    multiline
                    minRows={6}
                    fullWidth
                    noHeight
                    placeholder="Describe your product — material, dimensions, use case, story..."
                    value={form.description}
                    onChange={update("description")}
                    borderColor="#E5E7EB"
                  />

                  <div>
                    <MaInput
                      label={
                        <span>
                          Tags
                          {REQ}
                        </span>
                      }
                      fullWidth
                      placeholder="Comma-separated, e.g. handmade, gifts"
                      value={form.tags}
                      onChange={update("tags")}
                      borderColor="#E5E7EB"
                    />
                    <p className="mt-1.5 text-[12px] text-[#9CA3AF]">
                      Helps buyers discover your product in search
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-[#EFEFEF] bg-white p-6 md:p-8">
                <h2 className="my-0 mb-6 text-[15px] font-semibold text-[#1F2937]">
                  Pricing &amp; Inventory
                </h2>

                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <MaInput
                      label={
                        <span>
                          Price ($)
                          {REQ}
                        </span>
                      }
                      fullWidth
                      placeholder="$0"
                      value={form.price}
                      onChange={update("price")}
                      borderColor="#E5E7EB"
                    />
                    <MaInput
                      label="Discount (optional)"
                      fullWidth
                      placeholder="Enter discount percentage"
                      value={form.discount}
                      onChange={update("discount")}
                      borderColor="#E5E7EB"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <MaInput
                      label="Stock Quantity"
                      fullWidth
                      placeholder="0"
                      value={form.stock}
                      onChange={update("stock")}
                      borderColor="#E5E7EB"
                    />
                    <MaInput
                      label={
                        <span>
                          Low Stock Alert
                          {REQ}
                        </span>
                      }
                      fullWidth
                      placeholder="10"
                      value={form.lowStockAlert}
                      onChange={update("lowStockAlert")}
                      borderColor="#E5E7EB"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <MaInput
                      label={
                        <span>
                          Delivery Processing Time
                          {REQ}
                        </span>
                      }
                      fullWidth
                      placeholder="e.g. 2 (days)"
                      value={form.deliveryTime}
                      onChange={update("deliveryTime")}
                      borderColor="#E5E7EB"
                    />
                    <MaInput
                      label="Weight (kg)"
                      fullWidth
                      placeholder="0"
                      value={form.weight}
                      onChange={update("weight")}
                      borderColor="#E5E7EB"
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-[#F3F4F6] bg-[#FAFAFA] px-4 py-3">
                    <Typography className="text-[13px] font-semibold text-[#374151]">
                      Product Status (local only)
                    </Typography>

                    <MaSwitch
                      checked={form.listingActive}
                      onChange={updateBool("listingActive")}
                    />
                  </div>
                </div>
              </section>

              {submitError ? (
                <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-[#B91C1C] mx-20">
                  {submitError}
                </p>
              ) : null}

              <div className="flex flex-wrap justify-end gap-3 pt-2 mr-10">
                <MaButton
                  title="Back"
                  onClick={() => navigate("/seller/my-products")}
                  disabled={submitting}
                  sx={{
                    bgcolor: "#F3F4F6",
                    color: "#1F2937",
                    border: "none",
                    boxShadow: "none",
                
                 
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#E5E7EB" },
                  }}
                />
                <MaButton
                  variant="primary"
                  title={submitting ? "Saving…" : "Save changes"}
                  onClick={handleSave}
                  disabled={submitting}
                  sx={{
                   
                 
                    fontWeight: 600,
                    bgcolor: "#1A1625",
                    "&:hover": { bgcolor: "#2E1834" },
                  }}
                />
              </div>
            </div>
          </Grid2>
          <Grid2 size={{ md: 5 }}>
            <aside className="flex bg-white rounded-[16px] py-4 flex-col gap-3 lg:col-span-4">
              <div className="rounded-2xl mx-4 border border-solid border-[#E5E7EB80] bg-[#F8F8F8] p-4 md:p-5">
                <div className="flex gap-3">
                  <MdLightbulb
                    className="mt-0.5 shrink-0 text-[24px] text-amber-500"
                    aria-hidden
                  />
                  <div>
                    <p className="my-0 text-[13px] font-semibold text-[#3D3D3D]">
                      Editing: {displayName}
                    </p>
                    <p className="my-0 mt-1 text-[12px] leading-relaxed text-[#626262]">
                      Saving changes to a rejected product will reset its status
                      to Pending for re-review.
                    </p>
                  </div>
                </div>
              </div>

              <section className="rounded-2xl border border-[#EFEFEF] bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.06)] md:p-7">
                <h3 className="my-0 mb-5 text-[15px] font-semibold text-[#1F2937]">
                  Product Media
                </h3>

                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple
                  className="hidden"
                  onChange={onPickImages}
                />
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo"
                  multiple
                  className="hidden"
                  onChange={onPickVideos}
                />

                <div className="mb-6">
                  <p className="my-0 mb-2 flex items-center gap-2 text-[13px] font-semibold text-[#374151]">
                    <HiOutlinePhotograph className="text-[18px] text-[#6B7280]" />
                    Product Images
                    <span className="font-normal text-[#9CA3AF]">
                      (First image is cover — max {MAX_IMAGES})
                    </span>
                  </p>
                  <button
                    type="button"
                    disabled={imageSlotsLeft <= 0 || submitting}
                    onClick={() => imageInputRef.current?.click()}
                    className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-4 py-10 text-center transition-colors hover:border-[#9CA3AF] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                             <img src={gallery} alt="" className="h-7 w-7 opacity-80" />
                    <span className="text-[13px] font-medium text-[#4B5563]">
                      {imageSlotsLeft > 0
                        ? "Add images (Cloudinary on save)"
                        : "Maximum images reached"}
                    </span>
                    <span className="text-[12px] text-[#9CA3AF]">
                      JPEG, PNG, WebP — max ~5MB each
                    </span>
                  </button>

                  {serverImageUrls.length + pendingImages.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {serverImageUrls.map((src, index) => (
                        <div
                          key={`srv-${src}`}
                          className="relative h-24 w-24 overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F3F4F6]"
                        >
                          <img
                            src={src}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          {index === 0 ? (
                            <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                              Cover
                            </span>
                          ) : null}
                          <button
                            type="button"
                            disabled={submitting || imageBusyUrl === src}
                            onClick={() => removeServerImage(src)}
                            className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#DC2626] shadow-sm hover:bg-white disabled:opacity-50"
                            aria-label="Remove image from product"
                          >
                            <IoClose className="text-lg" />
                          </button>
                        </div>
                      ))}
                      {pendingImages.map((item, index) => (
                        <div
                          key={item.id}
                          className="relative h-24 w-24 overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F3F4F6]"
                        >
                          <img
                            src={item.preview}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                          {serverImageUrls.length + index === 0 ? (
                            <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                              Cover
                            </span>
                          ) : null}
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => removePendingImage(item.id)}
                            className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-[#DC2626] shadow-sm hover:bg-white"
                            aria-label="Remove new image"
                          >
                            <IoClose className="text-lg" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="mb-6">
                  <p className="my-0 mb-2 flex flex-wrap items-center gap-2 text-[13px] font-semibold text-[#374151]">
                    <FiFilm className="text-[18px] text-[#6B7280]" />
                    Product Videos
                    <span className="font-normal text-[#9CA3AF]">
                      (Optional — max {MAX_VIDEOS} total)
                    </span>
                  </p>
                  <button
                    type="button"
                    disabled={submitting || videoCount >= MAX_VIDEOS}
                    onClick={() => videoInputRef.current?.click()}
                    className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-4 py-10 text-center transition-colors hover:border-[#9CA3AF] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                           <img src={gallery} alt="" className="h-7 w-7 opacity-80" />
                    <span className="text-[13px] font-medium text-[#4B5563]">
                      Add video files
                    </span>
                    <span className="text-[12px] text-[#9CA3AF]">
                      MP4, MOV, AVI — uploaded on save
                    </span>
                  </button>

                  {serverVideoUrls.length > 0 ? (
                    <ul className="mt-3 space-y-2 text-[12px] text-[#374151]">
                      {serverVideoUrls.map((url) => (
                        <li
                          key={url}
                          className="flex items-start justify-between gap-2 rounded-lg border border-[#F3F4F6] bg-[#FAFAFA] px-2 py-2"
                        >
                          <span className="min-w-0 flex-1 truncate" title={url}>
                            {url}
                          </span>
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => removeServerVideo(url)}
                            className="shrink-0 text-[#DC2626]"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {pendingVideos.length > 0 ? (
                    <ul className="mt-2 space-y-2 text-[12px] text-[#374151]">
                      {pendingVideos.map((v) => (
                        <li
                          key={v.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-[#F3F4F6] px-2 py-2"
                        >
                          <span className="truncate">{v.file.name}</span>
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => removePendingVideo(v.id)}
                            className="shrink-0 text-[#DC2626]"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <div>
                  <MaInput
                    label="Embed video URLs"
                    multiline
                    minRows={2}
                    fullWidth
                    noHeight
                    placeholder="YouTube or other URLs — one per line or comma-separated"
                    value={form.embedVideoUrls}
                    onChange={update("embedVideoUrls")}
                    borderColor="#E5E7EB"
                  />
                  <p className="mt-1 text-[12px] text-[#9CA3AF]">
                    Counts toward the {MAX_VIDEOS} video limit with files above.
                  </p>
                </div>
              </section>
            </aside>
          </Grid2>
        </Grid2>
      </div>
    </div>
  );
}
